import {
  createContext,
  HTMLAttributes,
  PropsWithChildren,
  RefObject,
  useContext,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import {AppContext, AppContextType} from "../app/AppContext";

export * from "./otsThemeTokens";
import {getOtsThemeVariables, OtsBrand, OtsColorScheme, OtsColorSchemePreference} from "./otsThemeTokens";

const OtsColorSchemeContext = createContext<OtsColorScheme | undefined>(undefined);
const OtsBrandContext = createContext<OtsBrand | undefined>(undefined);

/** The scheme of the enclosing theme root, or undefined outside one. */
export const useOtsColorScheme = (): OtsColorScheme | undefined => useContext(OtsColorSchemeContext);

/** The brand of the enclosing theme root, or undefined outside one. */
export const useOtsBrand = (): OtsBrand | undefined => useContext(OtsBrandContext);

// A shadow root has no parent of its own; its host carries on up the page. GWT mounts inside one.
const parentAcrossShadow = (node: Node): Node | null => {
  const parent = node.parentNode;
  return parent && parent.nodeType === 11 && "host" in parent ? (parent as ShadowRoot).host : parent;
};

const hostSchemeOf = (element: Element): OtsColorScheme | undefined => {
  for (const value of [element.getAttribute("data-theme"), element.getAttribute("data-bs-theme")]) {
    if (value === "dark" || value === "light") {
      return value;
    }
  }
  if (element.classList.contains("dark")) {
    return "dark";
  }
  if (element.classList.contains("light")) {
    return "light";
  }
  return undefined;
};

const systemPrefersDark = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

/** The host page's theme above `start` -- the nearest themed ancestor, else the operating system's. */
export const resolveOtsHostColorScheme = (start: Node | null): OtsColorScheme => {
  for (let node = start; node; node = parentAcrossShadow(node)) {
    if (node.nodeType === 1) {
      const scheme = hostSchemeOf(node as Element);
      if (scheme) {
        return scheme;
      }
    }
  }
  return systemPrefersDark() ? "dark" : "light";
};

const hostThemeAttributes = ["class", "data-theme", "data-bs-theme"];

const useHostColorScheme = (enabled: boolean, ref: RefObject<HTMLElement>): OtsColorScheme => {
  // First guess from the page itself: a popover or dialog renders before it is attached, and guessing from the
  // operating system alone painted a light frame under a dark app (and the reverse) until it was.
  const [scheme, setScheme] = useState<OtsColorScheme>(() =>
    typeof document !== "undefined" && document.body ? resolveOtsHostColorScheme(document.body) : systemPrefersDark() ? "dark" : "light");

  // Before paint, so a dark host never flashes the light palette.
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const update = () => {
      const element = ref.current;
      setScheme(resolveOtsHostColorScheme(element ? parentAcrossShadow(element) : null));
    };

    // Hosts change theme at runtime: a toggle flips a class, or the OS switches at dusk.
    const media = typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : undefined;
    media?.addEventListener?.("change", update);

    const observer = typeof MutationObserver === "function" ? new MutationObserver(update) : undefined;
    // Popovers and dialogs (Headless UI portals) render before they are attached to the page. Read the host
    // theme only once this root is in the document; read from a detached tree it finds no theme, falls
    // back to the operating system's, and would never look again.
    let frame = 0;
    let attempts = 0;
    const attach = () => {
      const element = ref.current;
      if (element && !element.isConnected && attempts++ < 60 && typeof requestAnimationFrame === "function") {
        // Until it is attached, follow the page it is about to join.
        if (attempts === 1 && typeof document !== "undefined" && document.body) setScheme(resolveOtsHostColorScheme(document.body));
        frame = requestAnimationFrame(attach);
        return;
      }
      update();
      // Observe only ancestors, including the GWT shell above a shadow root.
      // This also picks up a page-scoped theme toggle without watching our own token updates.
      for (let node = element ? parentAcrossShadow(element) : null; observer && node; node = parentAcrossShadow(node)) {
        if (node.nodeType === 1) {
          observer.observe(node, {attributes: true, attributeFilter: hostThemeAttributes});
        }
      }
    };
    attach();

    return () => {
      if (frame && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
      media?.removeEventListener?.("change", update);
      observer?.disconnect();
    };
  }, [enabled, ref]);

  return scheme;
};

export type OtsThemeRootProps = PropsWithChildren<Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  /**
   * Always wins when given. Otherwise the enclosing theme root's brand, then the app's
   * (`AppContextProvider`), then corporate.
   */
  brand?: OtsBrand;
  /** Always wins when given. Otherwise the app's scheme, then `defaultColorScheme`. */
  colorScheme?: OtsColorSchemePreference;
  /**
   * The component's own choice, used only when neither it was given a scheme nor the app sets one.
   * Defaults to "inherit": an enclosing React theme, otherwise the host page or operating system.
   */
  defaultColorScheme?: OtsColorSchemePreference;
}>;

export const OtsThemeRoot = ({
  brand: brandProp,
  colorScheme: colorSchemeProp,
  defaultColorScheme = "inherit",
  className,
  style,
  children,
  ...props
}: OtsThemeRootProps) => {
  const ref = useRef<HTMLDivElement>(null);
  // Outside an AppContextProvider (stories, tests) there is no app to ask.
  const app = useContext(AppContext) as AppContextType | undefined;
  const enclosingBrand = useOtsBrand();
  const brand: OtsBrand = brandProp ?? enclosingBrand ?? app?.brand ?? "corporate";
  const inherited = useOtsColorScheme();
  // The app's "system" means "follow the host page". Inside a theme root that already did, follow it rather
  // than reading the page again, so a nested root (often a portalled panel) can never disagree with its parent.
  const appScheme = app?.colorScheme === "system" && inherited !== undefined ? "inherit" : app?.colorScheme;
  const colorScheme: OtsColorSchemePreference = colorSchemeProp ?? appScheme ?? defaultColorScheme;
  const hostScheme = useHostColorScheme(
    colorScheme === "system" || (colorScheme === "inherit" && inherited === undefined),
    ref
  );

  const resolved: OtsColorScheme =
    colorScheme === "light" || colorScheme === "dark" ? colorScheme
      : colorScheme === "system" ? hostScheme
        : inherited ?? hostScheme;

  return (
    <OtsBrandContext.Provider value={brand}>
    <OtsColorSchemeContext.Provider value={resolved}>
      <div
        {...props}
        ref={ref}
        data-ots-brand={brand}
        data-ots-color-scheme={resolved}
        className={className}
        style={{
          ...getOtsThemeVariables(brand, resolved),
          // Native controls, scrollbars, and autofill follow along.
          colorScheme: resolved,
          fontFamily: "var(--ots-font-body)",
          color: "var(--ots-text)",
          ...style
        }}>
        {children}
      </div>
    </OtsColorSchemeContext.Provider>
    </OtsBrandContext.Provider>
  );
};
